import React, { useMemo } from "react";
import type { FC } from "react";

import cn from "classnames";

import { MapLoaderSizeToTextSize } from "./constants/sizes";
import { getChildrenRecord } from "../../utils/react";

import type { LoaderProps } from "./types";

import classes from "./Loader.styl";
import LoaderIcon from "./assets/LoaderIcon.svg";

export const LOADER_CONTAINER_TEST_ID = "loader-container-test-id";
export const LOADER_ICON_TEST_ID = "loader-icon-test-id";
export const LOADER_TEST_ID = "loader-test-id";
const LOADER_DISPLAY_NAME = "Loader";

/**
 * Индикатор загрузки
 *
 * <a href="https://www.figma.com/design/vTg3KAvmAXA9LigeyiqCWL/%F0%9F%92%A0-Base-Components?m=auto&node-id=2-81877" target="_blank">Спека</a>
 * <br><br>
 * ### Дочерние компоненты
 *
 * | Компонент | Использование |
 * |-----------|----------|
 * | `<Loader.Label />` | Компонент, отвечающий за значение текста label. Выполнен на основе компонента TextCore. При size="custom" размер Label можно задать через проп "typographyStyle". |
 * @constructor
 */
export const Loader: FC<LoaderProps> = (props: LoaderProps): JSX.Element => {
    const {
        size = "24",
        view = "primary",
        direction = "horizontal",
        alignmentX = "center",
        alignmentY = "center",
        width = "100%",
        height = "100%",
        label,
        "data-tsid": tsid,
        children,
        customVarsStyles,
        customColorsStyles,
    } = props;

    const labelTypographyStyle =
        size !== "custom" && MapLoaderSizeToTextSize[size];

    const { Label } = useMemo(() => {
        return getChildrenRecord(children, "Loader", {
            Label: {
                props: {
                    ...(labelTypographyStyle && {
                        typographyStyle: labelTypographyStyle,
                    }),
                    customVarsStyles: classes["loader-label-container"],
                },
            },
        });
    }, [children, labelTypographyStyle]);

    const loaderClassName = cn(
        classes["loader"],
        classes[`__alignment-x-${alignmentX}`],
        classes[`__alignment-y-${alignmentY}`],
        classes[`__view-${view}`],
        view === "custom" && customColorsStyles,
    );

    const loaderContainerClassName = cn(
        classes["loader-container"],
        classes[`__size-${size}`],
        classes[`__direction-${direction}`],
        {
            [classes["__has-label"]]: !!Label,
        },
        size === "custom" && customVarsStyles,
    );

    const iconContainerClassName = cn(
        classes["loader-icon-container"],
        classes[`__size-${size}`],
    );

    return (
        <div
            className={loaderClassName}
            role="status"
            aria-label={label}
            data-tsid={tsid || LOADER_TEST_ID}
            data-uikit={LOADER_DISPLAY_NAME}
            style={{
                width,
                height,
            }}
        >
            <div className={loaderContainerClassName}>
                <div
                    className={iconContainerClassName}
                    data-tsid={LOADER_CONTAINER_TEST_ID}
                >
                    <LoaderIcon
                        className={classes["loader-icon"]}
                        data-tsid={LOADER_ICON_TEST_ID}
                    />
                </div>
                {Label}
            </div>
        </div>
    );
};

Loader.displayName = LOADER_DISPLAY_NAME;

export default Loader;
